import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg, publicImg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { initials } from "../../core/format";
import Modal from "../../components/Modal";
import Table from "../../components/Table";
import FormFields, { validateRequired } from "../../components/FormFields";
import ImageField from "../../components/ImageField";

export default function Employees() {
  const { toast, confirm } = useApp();
  const [rows, setRows] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([api.get("/employees?all=1"), api.get("/positions")]);
      setRows(e.data.items); setPositions(p.data.items);
    } catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const fields = [
    { name: "firstName", label: "نام", required: true },
    { name: "lastName", label: "نام خانوادگی", required: true },
    { name: "nationalId", label: "کد ملی", type: "nid", required: true },
    { name: "positionId", label: "سمت", type: "select", options: positions.map((p) => ({ value: p.id, label: p.name })), hint: positions.length ? undefined : "ابتدا از بخش «سمت‌ها» سمت تعریف کنید" },
  ];

  const openNew = () => { setForm({}); setErrors({}); setFormErr(""); setModal({ row: null }); };
  const openEdit = (r) => { setForm({ ...r, positionId: r.positionId ?? "" }); setErrors({}); setFormErr(""); setModal({ row: r }); };

  const save = async () => {
    const errs = validateRequired(fields, form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setFormErr("");
    try {
      const body = { firstName: form.firstName, lastName: form.lastName, nationalId: form.nationalId, positionId: form.positionId || "" };
      if (modal.row) {
        await api.put(`/employees/${modal.row.id}`, body);
        toast("تغییرات ذخیره شد");
        setModal(null);
      } else {
        const { data } = await api.post("/employees", body);
        toast("کارمند ثبت شد؛ می‌توانید عکس پرسنلی را اضافه کنید");
        setModal({ row: data.item }); // برای افزودن عکس
        setForm({ ...data.item, positionId: data.item.positionId ?? "" });
      }
      load();
    } catch (e) { setFormErr(errMsg(e)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (blob) => {
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", blob, "photo.png");
      const { data } = await api.post(`/employees/${modal.row.id}/photo`, fd);
      setModal((m) => ({ row: { ...m.row, photo: data.photo } }));
      toast("عکس ذخیره شد"); load();
    } catch (e) { toast(errMsg(e), "err"); } finally { setPhotoBusy(false); }
  };
  const removePhoto = async () => {
    setPhotoBusy(true);
    try { await api.delete(`/employees/${modal.row.id}/photo`); setModal((m) => ({ row: { ...m.row, photo: null } })); load(); }
    catch (e) { toast(errMsg(e), "err"); } finally { setPhotoBusy(false); }
  };

  const remove = async (r) => {
    if (!(await confirm(`«${r.firstName} ${r.lastName}» با تمام شیفت‌ها و وظایف ثبت‌شده‌اش حذف شود؟ حساب کاربری او نیز غیرفعال می‌شود.`))) return;
    try { await api.delete(`/employees/${r.id}`); toast("حذف شد"); load(); } catch (e) { toast(errMsg(e), "err"); }
  };

  const shown = rows.filter((r) => !q || `${r.firstName} ${r.lastName} ${r.nationalId}`.includes(q.trim()));

  return (
    <>
      <div className="page-head">
        <div><h2>کارکنان</h2><p>مشخصات پرسنل ایمنی و آتش‌نشانی بندر</p></div>
        <button className="btn" onClick={openNew}><i className="fa-solid fa-plus" /> افزودن کارمند</button>
      </div>
      <div className="toolbar"><div className="field"><input placeholder="جستجوی نام یا کد ملی..." value={q} onChange={(e) => setQ(e.target.value)} /></div></div>
      <div className="card">
        <Table loading={loading} rows={shown} empty="کارمندی ثبت نشده است"
          columns={[
            { key: "photo", label: "", render: (r) => r.photo ? <img className="avatar" src={publicImg("employees", r.photo)} alt="" /> : <span className="avatar">{initials(`${r.firstName} ${r.lastName}`)}</span> },
            { key: "name", label: "نام و نام خانوادگی", render: (r) => <b>{r.firstName} {r.lastName}</b> },
            { key: "nationalId", label: "کد ملی", render: (r) => <span dir="ltr">{r.nationalId}</span> },
            { key: "positionName", label: "سمت" },
            { key: "username", label: "حساب کاربری", render: (r) => r.username ? <span className="badge ok" dir="ltr">{r.username}</span> : <span className="badge wait">ندارد</span> },
          ]}
          actions={(r) => (
            <>
              <button className="icon-btn" title="ویرایش" onClick={() => openEdit(r)}><i className="fa-solid fa-pen" /></button>
              <button className="icon-btn danger" title="حذف" onClick={() => remove(r)}><i className="fa-solid fa-trash" /></button>
            </>
          )} />
      </div>

      {modal && (
        <Modal title={modal.row ? "ویرایش کارمند" : "افزودن کارمند"} onClose={() => !saving && setModal(null)}
          footer={<><button className="btn" onClick={save} disabled={saving}>{saving ? "در حال ذخیره..." : modal.row ? "ذخیره تغییرات" : "ثبت"}</button><button className="btn btn-ghost" onClick={() => setModal(null)}>بستن</button></>}>
          {formErr && <div className="alert err">{formErr}</div>}
          <FormFields fields={fields} form={form} setForm={setForm} errors={errors} />
          {modal.row ? (
            <ImageField label="عکس پرسنلی" round aspect={1} preview={publicImg("employees", modal.row.photo)} busy={photoBusy}
              onPick={uploadPhoto} onRemove={removePhoto} onError={(m) => toast(m, "err")} />
          ) : (
            <div className="hint" style={{ marginBottom: 12 }}>عکس پرسنلی پس از ثبت اولیه قابل افزودن است.</div>
          )}
        </Modal>
      )}
    </>
  );
}
